import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import useMenuConfig from '../../../hooks/useMenuConfig';
import { 
  Settings, 
  Save, 
  RotateCcw, 
  Eye, 
  EyeOff, 
  ChevronUp, 
  ChevronDown,
  Palette,
  Type,
  ToggleLeft,
  ToggleRight
} from 'lucide-react';

const MenuConfigManager = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { 
    menuConfig, 
    loading, 
    error, 
    updateMenuCustomization, 
    resetMenuConfig, 
    getAvailableMenuItems 
  } = useMenuConfig();

  const [availableItems, setAvailableItems] = useState(null);
  const [customizations, setCustomizations] = useState({});
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [activeTab, setActiveTab] = useState('main');

  // Color options for menu items
  const colorOptions = [
    { value: 'text-blue-600', label: 'Blue', color: '#2563eb' },
    { value: 'text-green-600', label: 'Green', color: '#16a34a' },
    { value: 'text-purple-600', label: 'Purple', color: '#9333ea' },
    { value: 'text-yellow-600', label: 'Yellow', color: '#ca8a04' },
    { value: 'text-red-600', label: 'Red', color: '#dc2626' },
    { value: 'text-cyan-600', label: 'Cyan', color: '#0891b2' },
    { value: 'text-orange-600', label: 'Orange', color: '#ea580c' },
    { value: 'text-teal-600', label: 'Teal', color: '#0d9488' },
    { value: 'text-gray-600', label: 'Gray', color: '#4b5563' }
  ];

  useEffect(() => {
    const loadAvailableItems = async () => {
      try {
        const items = await getAvailableMenuItems();
        setAvailableItems(items);
        
        // Initialize customizations from current config
        const initialCustomizations = {};
        if (menuConfig) {
          ['mainNavigation', 'adminNavigation'].forEach(navType => {
            if (menuConfig[navType]?.items) {
              menuConfig[navType].items.forEach(item => {
                if (item.label || item.color !== items[navType]?.items?.find(i => i.id === item.id)?.color) {
                  initialCustomizations[`${navType}-${item.id}`] = {
                    label: item.label || '',
                    color: item.color,
                    enabled: item.enabled,
                    order: item.order
                  };
                }
              });
            }
          });
        }
        setCustomizations(initialCustomizations);
      } catch (error) {
        console.error('Error loading available items:', error);
      }
    };

    if (user?.tenantCode) {
      loadAvailableItems();
    }
  }, [user?.tenantCode, menuConfig, getAvailableMenuItems]);

  const handleCustomizationChange = (navType, itemId, field, value) => {
    const key = `${navType}-${itemId}`;
    setCustomizations(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const promises = [];
      
      Object.entries(customizations).forEach(([key, customization]) => {
        const [navType, itemId] = key.split('-');
        if (Object.keys(customization).length > 0) {
          promises.push(
            updateMenuCustomization(navType, itemId, customization)
          );
        }
      });

      await Promise.all(promises);
      alert('Menu configuration saved successfully!');
    } catch (error) {
      console.error('Error saving menu configuration:', error);
      alert('Error saving menu configuration: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (window.confirm('Are you sure you want to reset all menu customizations to global defaults?')) {
      try {
        await resetMenuConfig();
        setCustomizations({});
        alert('Menu configuration reset successfully!');
      } catch (error) {
        console.error('Error resetting menu configuration:', error);
        alert('Error resetting menu configuration: ' + error.message);
      }
    }
  };

  const moveItem = (navType, itemId, direction) => {
    const key = `${navType}-${itemId}`;
    const currentOrder = customizations[key]?.order || 
      availableItems?.[navType]?.items?.find(item => item.id === itemId)?.order || 0;
    
    const newOrder = direction === 'up' ? currentOrder - 1 : currentOrder + 1;
    handleCustomizationChange(navType, itemId, 'order', newOrder);
  };

  const renderMenuItem = (item, navType) => {
    const key = `${navType}-${item.id}`;
    const customization = customizations[key] || {};
    const isEnabled = customization.enabled !== undefined ? customization.enabled : item.enabled;
    const displayLabel = customization.label || item.labelKey || item.id;
    const displayColor = customization.color || item.color;

    return (
      <div key={item.id} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => moveItem(navType, item.id, 'up')}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                title="Move up"
              >
                <ChevronUp className="h-4 w-4" />
              </button>
              <button
                onClick={() => moveItem(navType, item.id, 'down')}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                title="Move down"
              >
                <ChevronDown className="h-4 w-4" />
              </button>
            </div>
            <h3 className="font-medium text-gray-900 dark:text-gray-100">{item.id}</h3>
            <span className="text-sm text-gray-500 dark:text-gray-400">({item.path})</span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handleCustomizationChange(navType, item.id, 'enabled', !isEnabled)}
              className={`flex items-center space-x-2 px-3 py-1 rounded-md text-sm ${
                isEnabled 
                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                  : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
              }`}
            >
              {isEnabled ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
              {isEnabled ? 'Enabled' : 'Disabled'}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Custom Label */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <Type className="h-4 w-4 inline mr-1" />
              Custom Label
            </label>
            <input
              type="text"
              value={customization.label || ''}
              onChange={(e) => handleCustomizationChange(navType, item.id, 'label', e.target.value)}
              placeholder={item.labelKey || item.id}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
            />
          </div>

          {/* Color Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <Palette className="h-4 w-4 inline mr-1" />
              Color
            </label>
            <div className="flex flex-wrap gap-2">
              {colorOptions.map(colorOption => (
                <button
                  key={colorOption.value}
                  onClick={() => handleCustomizationChange(navType, item.id, 'color', colorOption.value)}
                  className={`w-8 h-8 rounded-full border-2 ${
                    displayColor === colorOption.value ? 'border-gray-900 dark:border-white' : 'border-gray-300 dark:border-gray-600'
                  }`}
                  style={{ backgroundColor: colorOption.color }}
                  title={colorOption.label}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Preview */}
        {showPreview && (
          <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Preview:</div>
            <div className={`inline-flex items-center px-3 py-1 rounded-md text-sm font-medium ${displayColor}`}>
              <span>{displayLabel}</span>
            </div>
          </div>
        )}
      </div>
    );
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-gray-500 dark:text-gray-400">Loading menu configuration...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
        <div className="text-red-800 dark:text-red-200">Error: {error}</div>
        <button 
          onClick={() => window.location.reload()}
          className="mt-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!availableItems) {
    return (
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-4">
        <div className="text-yellow-800 dark:text-yellow-200">
          <p>No menu configuration available.</p>
          <p className="text-sm mt-2">User tenant: {user?.tenantCode || 'None'}</p>
          <p className="text-sm">Menu config: {menuConfig ? 'Loaded' : 'Not loaded'}</p>
        </div>
        <button 
          onClick={() => window.location.reload()}
          className="mt-2 px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
          Menu Configuration Manager
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Customize menu items, labels, colors, and ordering for your tenant.
        </p>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between mb-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setShowPreview(!showPreview)}
            className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm ${
              showPreview 
                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' 
                : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
            }`}
          >
            {showPreview ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            {showPreview ? 'Hide Preview' : 'Show Preview'}
          </button>
        </div>
        
        <div className="flex items-center space-x-3">
          <button
            onClick={handleReset}
            className="flex items-center space-x-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
          >
            <RotateCcw className="h-4 w-4" />
            Reset to Defaults
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Save className="h-4 w-4" />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('main')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'main'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
              }`}
            >
              Main Navigation
            </button>
            <button
              onClick={() => setActiveTab('admin')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'admin'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
              }`}
            >
              Admin Navigation
          </button>
          </nav>
        </div>
      </div>

      {/* Menu Items */}
      <div className="space-y-6">
        {activeTab === 'main' && (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Main Navigation Items
            </h2>
            {availableItems?.mainNavigation?.items
              ?.sort((a, b) => (customizations[`mainNavigation-${a.id}`]?.order || a.order) - (customizations[`mainNavigation-${b.id}`]?.order || b.order))
              ?.map(item => renderMenuItem(item, 'mainNavigation'))}
          </div>
        )}

        {activeTab === 'admin' && (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Admin Navigation Items
            </h2>
            {availableItems?.adminNavigation?.items
              ?.sort((a, b) => (customizations[`adminNavigation-${a.id}`]?.order || a.order) - (customizations[`adminNavigation-${b.id}`]?.order || b.order))
              ?.map(item => renderMenuItem(item, 'adminNavigation'))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MenuConfigManager;
